import { Transition } from '@headlessui/react';
import { Button, Text } from '@getkarla/frontend-framework';
import { CheckIcon, InformationCircleIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import React, { Fragment, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ToastState } from '~/redux/reducers/toast-reducer';

const PlatformToast = ({ type, show, label, description, onClose, onConfirm, confirmText = 'OK', cancelText = 'Cancel', autoClose = true, displayIcon = true, displayConfirm = false, displayCancel = false }: ToastState & { onClose: () => void }) => {
  useEffect(() => {
    if (!autoClose || !show) return;

    const timer = setTimeout(onClose, 10000);
    return () => clearTimeout(timer);
  }, [autoClose, onClose, show]);

  let Icon = CheckIcon;
  let iconColor = 'text-[var(--feedback-success-bg)]';

  if (type === 'error') {
    Icon = XCircleIcon;
    iconColor = 'text-[var(--feedback-danger-bg)]';
  } else if (type === 'info') {
    Icon = InformationCircleIcon;
    iconColor = 'text-[var(--feedback-info-bg)]';
  } else if (type === 'warning') {
    Icon = InformationCircleIcon;
    iconColor = 'text-[var(--feedback-warning-bg)]';
  }

  return (
    <Transition show={show} enter="transition ease-out duration-300 transform" enterFrom="opacity-0 translate-y-8" enterTo="opacity-100 translate-y-0" leave="transition ease-in duration-200 transform" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-8" as={Fragment}>
      <div className="karla-platform-toast fixed bottom-6 right-6 z-[12000] flex w-[min(420px,calc(100vw-2rem))] items-center gap-3 rounded-xl bg-[var(--text-base)] px-4 py-3 shadow-[var(--shadow-lg)]">
        {displayIcon && <Icon className={`size-5 shrink-0 ${iconColor}`} aria-hidden="true" />}

        <div className="min-w-0 flex-1">
          <Text variant="label" color="white" className="block min-w-0 break-words line-clamp-2">
            {label}
          </Text>
          {description && (
            <Text variant="body-sm" color="white" className="mt-0.5 block min-w-0 break-words line-clamp-2">
              {description}
            </Text>
          )}
        </div>

        {(displayConfirm || displayCancel) && (
          <div className="flex shrink-0 items-center gap-2">
            {displayCancel && <Button text={cancelText} variant="secondary" handleClick={onClose} />}
            {displayConfirm && onConfirm && <Button text={confirmText} variant="primary" handleClick={onConfirm} />}
          </div>
        )}

        <Button variant="tertiary" size="icon-sm" icon={<XMarkIcon />} iconColor="#FFFFFF" className="shrink-0 hover:!bg-white/10" handleClick={onClose} aria-label="Close notification" title="Close notification" />
      </div>
    </Transition>
  );
};

export const ToastRoot = () => {
  const dispatch = useDispatch();
  const toastState = useSelector((state: any) => state.toast as ToastState);

  return <PlatformToast {...toastState} onClose={() => dispatch({ type: 'REMOVE_TOAST' })} />;
};
